/**
 * What the keys, the pointer and the user's rights do in a library table
 * (AP-128) - pure, so it can be tested without a browser.
 */

/** Rows a Page Up / Page Down moves. */
export const PAGE_ROWS = 10;
/** How long a finger rests on a row before its menu opens, and how far it may wander. */
export const LONG_PRESS_MS = 500;
export const LONG_PRESS_SLOP = 10;

/** The selected row after a key, among `count` rows; null when the key does not move it. */
export function moveSelection(index: number | null, key: string, count: number): number | null {
  if (count <= 0) return null;
  const last = count - 1;
  const at = index === null ? -1 : Math.min(index, last);
  switch (key) {
    case 'ArrowDown': return Math.min(at + 1, last);
    case 'ArrowUp': return at < 0 ? 0 : Math.max(at - 1, 0);
    case 'Home': return 0;
    case 'End': return last;
    case 'PageDown': return Math.min(Math.max(at, 0) + PAGE_ROWS, last);
    case 'PageUp': return Math.max(at - PAGE_ROWS, 0);
    default: return null;
  }
}

export type RowKeyAction = 'open' | 'menu' | 'move' | null;

/** Enter opens the row, the context-menu key or Shift + F10 its menu, the arrows and friends move. */
export function rowKeyAction(event: { key: string; shiftKey: boolean }): RowKeyAction {
  if (event.key === 'Enter') return 'open';
  if (event.key === 'ContextMenu' || (event.key === 'F10' && event.shiftKey)) return 'menu';
  if (['ArrowDown', 'ArrowUp', 'Home', 'End', 'PageDown', 'PageUp'].includes(event.key)) return 'move';
  return null;
}

export type RowMenuEntry = 'view' | 'edit' | 'remove';

/**
 * The entries of a row's menu. Looking is for everyone; changing and removing
 * office-wide reference data only with library.manage (ADR 0020), and only
 * where the tab has a way to write at all. The server checks again.
 */
export function rowMenuEntries(canManage: boolean, ways: { editable: boolean; removable: boolean }): RowMenuEntry[] {
  return ['view', ...(canManage && ways.editable ? ['edit' as const] : []), ...(canManage && ways.removable ? ['remove' as const] : [])];
}

/** Runs `action` only once the user said yes - deleting is not undone. */
export function confirmThen(question: string, confirm: (q: string) => boolean, action: () => void): boolean {
  if (!confirm(question)) return false;
  action();
  return true;
}

/** Whether a finger moved too far for a long press. */
export function movedTooFar(start: { x: number; y: number }, now: { x: number; y: number }, slop = LONG_PRESS_SLOP): boolean {
  return Math.abs(now.x - start.x) > slop || Math.abs(now.y - start.y) > slop;
}

/**
 * Puts the focus back on a row once a popup or menu is gone - on the next
 * frame, after the popup has let go of it. Skipped when something else (a
 * popup opened from the menu) has taken over in the meantime.
 */
export function returnFocus(
  target: () => { focus: (options?: { preventScroll?: boolean }) => void } | null | undefined,
  still: () => boolean = () => true,
  schedule: (f: () => void) => void = (f) => { if (typeof requestAnimationFrame === 'function') requestAnimationFrame(f); else setTimeout(f, 0); },
): void {
  schedule(() => { if (still()) target()?.focus(); });
}
